// Site Editor — admin tab that lets the owner edit site content live without code.
// Reads/writes /api/admin/site-config and refreshes the public SiteConfigContext.

import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, Settings2, Type, Palette, Coins, FileText, Wrench, Tag } from "lucide-react";
import { toast } from "sonner";
import { adminClient } from "@/lib/api";
import { useSiteCfg, FONT_OPTIONS, DEFAULTS_MAP } from "@/lib/siteConfig";
import * as defaults from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Helpers to read/write nested fields immutably
function getPath(obj, path) {
    const parts = path.split(".");
    let cur = obj;
    for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return undefined;
    }
    return cur;
}
function setPath(obj, path, value) {
    const parts = path.split(".");
    const next = { ...(obj || {}) };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        cur[p] = { ...(cur[p] || {}) };
        cur = cur[p];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
}
function deletePath(obj, path) {
    const parts = path.split(".");
    const next = { ...(obj || {}) };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!cur[p] || typeof cur[p] !== "object") return next;
        cur[p] = { ...cur[p] };
        cur = cur[p];
    }
    delete cur[parts[parts.length - 1]];
    return next;
}

function Field({ label, path, draft, setDraft, fallback, placeholder, type = "text", area = false, hint }) {
    const cur = getPath(draft, path);
    const has = cur !== undefined && cur !== null;
    const onChange = (v) =>
        setDraft(v === "" || v === null ? deletePath(draft, path) : setPath(draft, path, v));
    const Component = area ? "textarea" : "input";
    return (
        <div>
            <div className="flex justify-between items-center">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">{label}</Label>
                {has && (
                    <button
                        type="button"
                        onClick={() => setDraft(deletePath(draft, path))}
                        className="text-[10px] text-zinc-500 hover:text-red-400 inline-flex items-center gap-1"
                        title="Revert to default"
                    >
                        <RotateCcw className="w-2.5 h-2.5" /> reset
                    </button>
                )}
            </div>
            <Component
                value={has ? String(cur) : ""}
                onChange={(e) => {
                    let v = e.target.value;
                    if (type === "number") {
                        v = v === "" ? "" : Number(v);
                        if (Number.isNaN(v)) v = "";
                    }
                    onChange(v);
                }}
                placeholder={placeholder ?? (fallback != null ? `default: ${fallback}` : "")}
                type={area ? undefined : type}
                rows={area ? 4 : undefined}
                data-testid={`site-editor-field-${path}`}
                className={`mt-1 w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 ${area ? "resize-y" : ""}`}
            />
            {hint && <p className="mt-1 text-[10px] text-zinc-500">{hint}</p>}
        </div>
    );
}

function ListField({ label, path, draft, setDraft, fallback, placeholder, hint }) {
    const cur = getPath(draft, path);
    const list = Array.isArray(cur) ? cur : null;
    const useList = list !== null;
    const setList = (next) => setDraft(setPath(draft, path, next));
    return (
        <div>
            <div className="flex justify-between items-center">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">{label}</Label>
                {useList ? (
                    <button type="button" onClick={() => setDraft(deletePath(draft, path))} className="text-[10px] text-zinc-500 hover:text-red-400 inline-flex items-center gap-1">
                        <RotateCcw className="w-2.5 h-2.5" /> reset
                    </button>
                ) : (
                    <button type="button" onClick={() => setList(Array.isArray(fallback) ? [...fallback] : [""])} className="text-[10px] text-red-400 hover:text-red-300">edit</button>
                )}
            </div>
            {useList && (
                <div className="mt-2 space-y-2">
                    {list.map((v, i) => (
                        <div key={i} className="flex gap-2">
                            <input
                                value={String(v ?? "")}
                                onChange={(e) => {
                                    const n = [...list]; n[i] = e.target.value; setList(n);
                                }}
                                data-testid={`site-editor-list-${path}-${i}`}
                                placeholder={placeholder || `Item ${i + 1}`}
                                className="flex-1 bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600"
                            />
                            <button type="button" onClick={() => setList(list.filter((_, j) => j !== i))} className="text-[10px] text-zinc-500 hover:text-red-400">remove</button>
                        </div>
                    ))}
                    <button type="button" onClick={() => setList([...list, ""])} className="text-[10px] text-red-400 hover:text-red-300">+ add item</button>
                </div>
            )}
            {hint && <p className="mt-1 text-[10px] text-zinc-500">{hint}</p>}
        </div>
    );
}

function Section({ icon: Icon, title, subtitle, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-sm border border-white/10 bg-zinc-950">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5"
                data-testid={`site-editor-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
                <Icon className="w-4 h-4 text-red-500" />
                <div className="flex-1">
                    <div className="font-display font-bold uppercase tracking-tight text-white text-sm">{title}</div>
                    {subtitle && <div className="text-[11px] text-zinc-500">{subtitle}</div>}
                </div>
                <span className="text-zinc-500 text-sm">{open ? "−" : "+"}</span>
            </button>
            {open && <div className="px-4 pb-5 pt-1 space-y-4 border-t border-white/5">{children}</div>}
        </div>
    );
}

export default function SiteEditor() {
    const { cfg, setCfg } = useSiteCfg();
    const [draft, setDraft] = useState(cfg || {});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        adminClient()
            .get("/admin/site-config")
            .then((r) => {
                if (!mounted) return;
                setDraft(r.data || {});
            })
            .catch(() => {
                if (mounted) setDraft(cfg || {});
            })
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await adminClient().put("/admin/site-config", draft);
            setCfg(draft);
            toast.success("Site updated — the website now reflects your changes.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const resetAll = async () => {
        if (!window.confirm("Reset ALL overrides? Site falls back to built-in defaults.")) return;
        setDraft({});
        setSaving(true);
        try {
            await adminClient().put("/admin/site-config", {});
            setCfg({});
            toast.success("All overrides cleared — site is back to defaults.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="mt-8 flex items-center gap-2 text-zinc-500" data-testid="site-editor-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
        );
    }

    const heroChipsDefault = ["Ford dealership tech", "Honest pricing", "Mobile — I come to you"];
    const aboutParagraphsDefault = [
        "I work professionally on cars every day at a Ford dealership here in Virginia. Limit Break Performance is the side I'm building on my own time — honest mobile work for people who don't want to deal with service advisors, markups, or up-sells.",
        "I grew up around cars, got trained on them professionally, and now I want to bring that experience straight to your driveway. No middleman. No corporate script. Just a technician who actually enjoys this stuff.",
    ];

    const fp = (path) => DEFAULTS_MAP[path];

    return (
        <div className="mt-6 space-y-4" data-testid="site-editor">
            <div className="rounded-sm border border-red-600/30 bg-red-950/10 p-4 flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-display font-black uppercase tracking-tight text-white text-lg flex items-center gap-2">
                        <Settings2 className="w-5 h-5 text-red-500" /> Site Editor
                    </h3>
                    <p className="text-[11px] text-zinc-400 mt-1">
                        Edit text, fonts, and colors live. Leave a field blank to use the built-in default.
                        Hit <span className="text-white font-semibold">Save</span> to publish — site updates immediately.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={resetAll}
                        className="text-xs text-zinc-400 hover:text-red-400 px-3 py-2 border border-white/10 rounded-sm inline-flex items-center gap-1"
                        data-testid="site-editor-reset-all"
                    >
                        <RotateCcw className="w-3 h-3" /> Reset all
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="site-editor-save"
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            <Section icon={Wrench} title="Business Info" subtitle="Brand, phone, service area, social links" defaultOpen>
                <Field label="Brand name" path="business.brand" draft={draft} setDraft={setDraft} fallback={fp("business.brand")} />
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Phone (display)" path="business.phone_display" draft={draft} setDraft={setDraft} fallback={fp("business.phone_display")} placeholder="(315) 719-5772" />
                    <Field label="Phone (E.164 / dial)" path="business.phone_e164" draft={draft} setDraft={setDraft} fallback={fp("business.phone_e164")} placeholder="+13157195772" hint="Used by Text / Call buttons. Must start with +countrycode." />
                </div>
                <Field label="Service area (short)" path="business.service_area" draft={draft} setDraft={setDraft} fallback={fp("business.service_area")} />
                <Field label="Service area (long)" path="business.service_area_long" draft={draft} setDraft={setDraft} fallback={fp("business.service_area_long")} />
                <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Instagram URL" path="business.instagram_url" draft={draft} setDraft={setDraft} fallback={fp("business.instagram_url")} />
                    <Field label="TikTok URL" path="business.tiktok_url" draft={draft} setDraft={setDraft} fallback={fp("business.tiktok_url")} />
                    <Field label="Facebook URL" path="business.facebook_url" draft={draft} setDraft={setDraft} fallback={fp("business.facebook_url")} />
                </div>
            </Section>

            <Section icon={Type} title="Home Hero" subtitle="Top of homepage — badge, title, subtitle, CTAs, chips">
                <Field label="Badge text (small bar above title)" path="hero.badge" draft={draft} setDraft={setDraft} fallback={`Mobile · ${defaults.SERVICE_AREA_LONG}`} />
                <Field label="Hero title" path="hero.title" draft={draft} setDraft={setDraft} fallback={defaults.BRAND} />
                <Field
                    area
                    label="Hero subtitle"
                    path="hero.subtitle"
                    draft={draft}
                    setDraft={setDraft}
                    fallback="Mobile mechanic & performance services in Virginia Beach. I come to you — brakes, coilovers, diagnostics, bolt-ons, done right in your driveway."
                />
                <Field label="Primary CTA button" path="hero.text_btn" draft={draft} setDraft={setDraft} fallback="Text Now For A Quote" />
                <ListField label="Trust chips under CTAs" path="hero.chips" draft={draft} setDraft={setDraft} fallback={heroChipsDefault} placeholder="Ford dealership tech" />
            </Section>

            <Section icon={FileText} title="About Page" subtitle="Eyebrow, title, paragraphs, how-I-work blocks">
                <Field label="Eyebrow (small label)" path="about.eyebrow" draft={draft} setDraft={setDraft} fallback="The guy behind the wrench" />
                <Field label="About title" path="about.title" draft={draft} setDraft={setDraft} fallback="Hey — I'm The Mechanic." />
                <ListField label="About paragraphs" path="about.paragraphs" draft={draft} setDraft={setDraft} fallback={aboutParagraphsDefault} placeholder="Write a paragraph…" hint="Each item becomes a paragraph on the About page." />
                <Field label="Credential chip (under paragraphs)" path="about.credential_chip" draft={draft} setDraft={setDraft} fallback="Ford dealership technician" />
                <Field label="About hero image URL" path="about.image_url" draft={draft} setDraft={setDraft} fallback="(default engine-out photo)" hint="Direct image URL. Leave blank to keep default." />
                <Field label="How-I-Work section title" path="about.how_title" draft={draft} setDraft={setDraft} fallback="How I Work" />
                <p className="text-[10px] text-zinc-500">How-I-Work blocks (3 of them) can be edited as a JSON list of {`{ t, d }`} objects via the "Advanced JSON" section at the bottom.</p>
            </Section>

            <Section icon={Coins} title="Stats Baseline" subtitle="Numbers shown on the homepage trust strip. You control when these change.">
                <p className="text-[11px] text-amber-300/80 bg-amber-950/20 border border-amber-700/30 px-3 py-2 rounded-sm">
                    These are the <b>starting</b> counts. Real leads + referrals collected through the website automatically add on top.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field type="number" label="Jobs completed (baseline)" path="stats_baseline.jobs_done" draft={draft} setDraft={setDraft} fallback={10} />
                    <Field type="number" label="Rev Cash paid out (baseline $)" path="stats_baseline.revcash_paid" draft={draft} setDraft={setDraft} fallback={50} />
                    <Field type="number" label="Total referrals (baseline)" path="stats_baseline.total_referrals" draft={draft} setDraft={setDraft} fallback={1} />
                    <Field type="number" label="Avg rating (used when no real reviews)" path="stats_baseline.avg_rating" draft={draft} setDraft={setDraft} fallback={5.0} hint="0–5. Real reviews override this once they come in." />
                </div>
            </Section>

            <Section icon={Coins} title="Rev Cash" subtitle="Referral reward amount">
                <Field type="number" label="Reward per referral ($)" path="rev_cash.amount" draft={draft} setDraft={setDraft} fallback={defaults.REV_CASH_AMOUNT} />
            </Section>

            <Section icon={Palette} title="Theme & Typography" subtitle="Fonts and brand color">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-400">Display font (titles, headlines)</Label>
                        <select
                            value={getPath(draft, "typography.display_font") || "Cabinet Grotesk"}
                            onChange={(e) => setDraft(setPath(draft, "typography.display_font", e.target.value))}
                            data-testid="site-editor-display-font"
                            className="mt-1 w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-sm text-white"
                        >
                            {FONT_OPTIONS.display.map((o) => (
                                <option key={o.v} value={o.v}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-400">Body font (paragraphs, UI)</Label>
                        <select
                            value={getPath(draft, "typography.body_font") || "Manrope"}
                            onChange={(e) => setDraft(setPath(draft, "typography.body_font", e.target.value))}
                            data-testid="site-editor-body-font"
                            className="mt-1 w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-sm text-white"
                        >
                            {FONT_OPTIONS.body.map((o) => (
                                <option key={o.v} value={o.v}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-400">Brand accent color</Label>
                    <div className="mt-1 flex items-center gap-3">
                        <input
                            type="color"
                            value={getPath(draft, "theme.accent_hex") || "#dc2626"}
                            onChange={(e) => setDraft(setPath(draft, "theme.accent_hex", e.target.value))}
                            data-testid="site-editor-accent-color"
                            className="h-10 w-16 cursor-pointer bg-zinc-950 border border-white/10 rounded-sm"
                        />
                        <span className="text-xs text-zinc-500">Default red is <span className="text-white">#dc2626</span>. Currently only affects CSS variable <code>--brand-accent</code> — most buttons still use the original red palette.</span>
                    </div>
                </div>
            </Section>

            <Section icon={FileText} title="Footer" subtitle="Tagline & copyright line">
                <Field label="Tagline (above service area)" path="footer.tagline" draft={draft} setDraft={setDraft} fallback="Mobile mechanic & performance services. I come to your driveway —" />
                <Field label="Copyright suffix" path="footer.copyright_suffix" draft={draft} setDraft={setDraft} fallback="All rights reserved." />
                <Field label="Bottom line (right side)" path="footer.bottom_line" draft={draft} setDraft={setDraft} fallback="Mobile mechanic · Hampton Roads, Virginia" />
            </Section>

            <Section icon={Wrench} title="Top-Level Services" subtitle="The 4 service tiles (Brakes, Suspension, Diagnostics, Installs)">
                {defaults.SERVICES.map((s) => (
                    <div key={s.slug} className="rounded-sm border border-white/10 p-3 bg-black space-y-2">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{s.slug}</div>
                        <Field label={`Title`} path={`services.${s.slug}.title`} draft={draft} setDraft={setDraft} fallback={s.title} />
                        <Field label="Short blurb" path={`services.${s.slug}.short`} draft={draft} setDraft={setDraft} fallback={s.short} area />
                        <Field label="Long description" path={`services.${s.slug}.long`} draft={draft} setDraft={setDraft} fallback={s.long} area />
                    </div>
                ))}
            </Section>

            <Section icon={Tag} title="Service Pricing Catalog" subtitle="Per-service price & duration overrides on the /services page">
                {defaults.SERVICE_CATALOG.map((c) => (
                    <div key={c.slug} className="grid sm:grid-cols-3 gap-2 items-end rounded-sm border border-white/5 p-2 bg-black">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 sm:col-span-3">
                            {c.title} <span className="text-zinc-700">· {c.category}</span>
                        </div>
                        <Field label="Title" path={`catalog.${c.slug}.title`} draft={draft} setDraft={setDraft} fallback={c.title} />
                        <Field label="Duration" path={`catalog.${c.slug}.duration`} draft={draft} setDraft={setDraft} fallback={c.duration} />
                        <Field label="Price" path={`catalog.${c.slug}.price`} draft={draft} setDraft={setDraft} fallback={c.price} />
                    </div>
                ))}
            </Section>

            <Section icon={FileText} title="Articles / Blog Posts" subtitle="Override existing posts — title, excerpt, date, body paragraphs">
                {defaults.BLOG_POSTS.map((p) => (
                    <div key={p.slug} className="rounded-sm border border-white/10 p-3 bg-black space-y-2">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.slug}</div>
                        <Field label="Title" path={`blog.${p.slug}.title`} draft={draft} setDraft={setDraft} fallback={p.title} />
                        <Field label="Excerpt" path={`blog.${p.slug}.excerpt`} draft={draft} setDraft={setDraft} fallback={p.excerpt} area />
                        <div className="grid sm:grid-cols-2 gap-2">
                            <Field label="Date (YYYY-MM-DD)" path={`blog.${p.slug}.date`} draft={draft} setDraft={setDraft} fallback={p.date} />
                            <Field label="Read time" path={`blog.${p.slug}.readTime`} draft={draft} setDraft={setDraft} fallback={p.readTime} />
                        </div>
                        <ListField label="Body paragraphs" path={`blog.${p.slug}.body`} draft={draft} setDraft={setDraft} fallback={p.body} placeholder="Paragraph text…" />
                    </div>
                ))}
            </Section>

            <Section icon={Settings2} title="Advanced JSON" subtitle="Power-user: edit the raw config blob">
                <textarea
                    rows={14}
                    value={JSON.stringify(draft, null, 2)}
                    onChange={(e) => {
                        try {
                            const parsed = JSON.parse(e.target.value);
                            setDraft(parsed);
                        } catch {
                            /* ignore until valid */
                        }
                    }}
                    data-testid="site-editor-raw-json"
                    className="w-full bg-zinc-950 border border-white/10 rounded-sm px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-red-600 font-mono"
                />
                <p className="text-[10px] text-zinc-500">Live-parses as you type. Invalid JSON is ignored until you fix it.</p>
            </Section>

            <div className="rounded-sm border border-white/10 bg-zinc-950 p-4 flex items-center justify-between gap-3">
                <span className="text-[11px] text-zinc-500">Don't forget to Save — your changes won't go live until then.</span>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="site-editor-save-bottom"
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? "Saving…" : "Save Changes"}
                </button>
            </div>
        </div>
    );
}
